import React from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Analytics } from 'hooks/useAnalytics'
import { useEnvironment } from 'hooks/useEnvironment'
import { GetSigner } from 'privy/WalletReady'
import { useLinkEOAToRootKeyTransaction } from 'use-towns-client'

import { mapToErrorMessage } from '../utils'

import { switchConnectedWalletChain } from './switchConnectedWalletChain'
import { usePrivyConnectWallet } from './usePrivyConnectWallet'

// this hook should only be used in desktop contexts in the case that a wallet extension is on the page
// and not in mobile contexts, since that involves switching back and forth between the app and wallet and some events might misfire
export function useConnectThenLink({
    onLinkWallet,
    getSigner,
}: {
    onLinkWallet: (
        ...args: Parameters<
            ReturnType<typeof useLinkEOAToRootKeyTransaction>['linkEOAToRootKeyTransaction']
        >
    ) => void
    getSigner: GetSigner
}) {
    const { baseChain } = useEnvironment()

    const connectWallet = usePrivyConnectWallet({
        onSuccess: async ({ wallet }) => {
            const rootSigner = await getSigner()
            if (!rootSigner) {
                createPrivyNotAuthenticatedNotification()
                return
            }
            Analytics.getInstance().track('connected wallet', {
                walletName: wallet.meta.name,
            })
            switchConnectedWalletChain({
                wallet,
                baseChain,
                onSuccess: (signer) => {
                    onLinkWallet(rootSigner, signer)
                },
            })
        },
        onError: (error) => {
            console.error('[useConnectThenLink] error connecting wallet', error)
            const errorMessage = mapToErrorMessage({
                error: new Error(error),
                source: 'useConnectThenLink privy connect error',
            })

            if (errorMessage === 'ACTION_REJECTED') {
                return
            }

            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message={`Please make sure your wallet supports and is connected to the ${baseChain.name} network.`}
                    subMessage={errorMessage}
                />
            ))
        },
    })

    return connectWallet
}
